import './Signup.scss';
import queryString from 'query-string';
import { withAppContext, AppContextType } from 'context/AppContext';
import { getEnglishUIDFromContext } from 'helpers/getEnglishUIDFromContext';
import { PrismicHTMLParser } from 'components/PrismicHTMLParser';
import { SupportedURLParams } from 'types';
import React, { useEffect, useState } from 'react';
import {
    SliceContext,
    PrismicRichTextFieldType,
    FluidGatsbyImageFieldType,
    BooleanOptionType,
} from '../types';
import { SignupDefault } from './SignupDefault';
import { SignupImage } from './SignupImage';
import { withRegistrationForm } from './withRegistrationForm';

export enum SignupTypeEnum {
    Default = 'default',
    Pricing = 'pricing',
    Master = 'master',
    Distribution = 'distribution',
}

export type SignupItem = {
    signup_text: PrismicRichTextFieldType | null;
    signup_image: FluidGatsbyImageFieldType | null;
};

export type SignupSlice = {
    slice_type: 'signup';
    primary: {
        signup_title: PrismicRichTextFieldType;
        signup_source_message?: PrismicRichTextFieldType;
        signup_content: PrismicRichTextFieldType;
        show_login_first: BooleanOptionType;
        signup_type: SignupTypeEnum;
        signup_copy: {
            document: [
                {
                    data: {
                        description_basic_plan: PrismicRichTextFieldType;
                        description_pro_plan: PrismicRichTextFieldType;
                        description_advanced_plan: PrismicRichTextFieldType;
                    };
                }
            ];
        } | null;
    };
    items: SignupItem[];
};

export const UTMSourceMessage = ({
    params,
    msg,
}: {
    params: SupportedURLParams;
    msg: PrismicRichTextFieldType;
}) => {
    const hasUTMSource = (params: SupportedURLParams) => params && params.utm_source;

    return hasUTMSource(params) ? (
        <div className="Signup-utmSource">
            <PrismicHTMLParser>{msg.html || ''}</PrismicHTMLParser>
        </div>
    ) : null;
};

type SignupProps = {
    context: AppContextType;
    slice: SignupSlice;
    location: Location;
    sliceContext: SliceContext;
    params: SupportedURLParams;
    iframeURL: string;
};

const validSignupTypes = Object.values(SignupTypeEnum).map(t => t.toLowerCase());
const LOGIN_UID_ENGLISH = 'login';
const COLLABORATOR_UID_ENGLISH = 'collaborator';

const SignupComponent = (props: SignupProps) => {
    const params: SupportedURLParams = queryString.parse(props.location.search);
    const sliceSignupType = props.slice.primary.signup_type || 'default';
    const URLVariant = params.homePage || '';

    const [requestedSignupType, setRequestedSignupType] = useState();

    useEffect(() => {
        setRequestedSignupType(
            !!params.plan ? SignupTypeEnum.Pricing.toLowerCase() : URLVariant.toLowerCase(),
        );
    }, [params.plan]);

    if (getEnglishUIDFromContext(props.context) === LOGIN_UID_ENGLISH) {
        return <SignupDefault {...props} />;
    }

    if (getEnglishUIDFromContext(props.context) === COLLABORATOR_UID_ENGLISH) {
        return <SignupImage {...props} />;
    }

    const isValidSignupType = validSignupTypes.includes(requestedSignupType);

    if (sliceSignupType === requestedSignupType) {
        // Display SignupImage if its the slice signup type matches the request type
        return <SignupImage {...props} />;
    } else if (
        sliceSignupType === SignupTypeEnum.Default &&
        (!isValidSignupType || !requestedSignupType)
    ) {
        return <SignupDefault {...props} />;
    } else {
        /**
         * Prismic will send N login slices of different types (distribution, mastering, default, ...)
         * We want to render the proper one and 'omit' the other ones.
         */
        return null;
    }
};

const Signup = withAppContext(withRegistrationForm(SignupComponent));

export { Signup, SignupProps };
